import React, { useEffect, useMemo, useState } from 'react';
import {
  PRODUCTION_CAPITAL_CAMPAIGN,
  commissionPackageShortfall,
  listActiveProductionCapitalPackages,
  quoteProductionCapitalCart,
  quoteProductionCapitalConversion,
} from '../../lib/productionCapitalCampaign.js';

function formatShares(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export interface CommissionPackageCartLine {
  packageId: string;
  name: string;
  qty: number;
  paidShares: number;
  investorBonus: number;
  investorTotal: number;
  sponsorBonus: number;
  costUsd: number;
  phasePrice: number;
}

export interface CommissionPackageCart {
  lines: CommissionPackageCartLine[];
  phasePrice: number;
  costUsd: number;
  paidShares: number;
  investorBonus: number;
  investorTotal: number;
  sponsorBonus: number;
  shortfall: number;
  leftover: number;
  ready: boolean;
}

interface Props {
  balance: number;
  pendingTopups?: number;
  phasePrice: number;
  disabled?: boolean;
  onChange?: (cart: CommissionPackageCart | null) => void;
  onConvert?: (cart: CommissionPackageCart) => void;
  onTopUp?: (cart: CommissionPackageCart) => void;
  submitLabel?: string;
}

export const CommissionPackageChooser: React.FC<Props> = ({
  balance,
  pendingTopups = 0,
  phasePrice,
  disabled = false,
  onChange,
  onConvert,
  onTopUp,
  submitLabel = 'Convert packages to shares',
}) => {
  const packages = useMemo(() => listActiveProductionCapitalPackages(), []);
  const maxQty = PRODUCTION_CAPITAL_CAMPAIGN.maxPackageQty || 100;
  const [qtyByPackage, setQtyByPackage] = useState<Record<string, number>>({});

  const setQty = (packageId: string, next: number) => {
    const qty = Math.max(0, Math.min(maxQty, Math.floor(Number(next) || 0)));
    setQtyByPackage((prev) => ({ ...prev, [packageId]: qty }));
  };

  const selected = packages
    .map((pkg) => ({ packageId: pkg.id, qty: qtyByPackage[pkg.id] || 0 }))
    .filter((line) => line.qty > 0);
  const quoted = quoteProductionCapitalCart(selected, phasePrice);
  const spendable = Math.max(0, balance);
  const shortfall = quoted ? commissionPackageShortfall(quoted.costUsd, spendable, pendingTopups) : 0;
  const ready = !!quoted && quoted.costUsd <= spendable + 0.000001;
  const leftover = ready && quoted ? Math.max(0, spendable - quoted.costUsd) : spendable;
  const cart: CommissionPackageCart | null = quoted
    ? {
        lines: quoted.lines.map((line) => ({
          packageId: line.packageId,
          name: line.name,
          qty: line.qty,
          paidShares: line.paidShares,
          investorBonus: line.investorBonus,
          investorTotal: line.investorTotal,
          sponsorBonus: line.sponsorBonus,
          costUsd: line.costUsd,
          phasePrice: line.phasePrice,
        })),
        phasePrice: quoted.phasePrice,
        costUsd: quoted.costUsd,
        paidShares: quoted.paidShares,
        investorBonus: quoted.investorBonus,
        investorTotal: quoted.investorTotal,
        sponsorBonus: quoted.sponsorBonus,
        shortfall,
        leftover,
        ready,
      }
    : null;

  useEffect(() => {
    onChange?.(cart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.costUsd, cart?.paidShares, cart?.shortfall, selected.map((line) => line.packageId + 'x' + line.qty).join('|'), balance, pendingTopups, phasePrice]);

  if (!Number.isFinite(phasePrice) || phasePrice <= 0) {
    return <p className="text-sm text-red-300">The current phase price is unavailable. Refresh the page and try again.</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-300">
        All packages are listed. Set a quantity on every package you want. Prices use the current phase price of ${phasePrice.toFixed(2)} per paid share.
        Commission you do not spend stays in your balance. If this list looks wrong, refresh the page.
      </p>
      <div className="grid grid-cols-1 gap-3">
        {packages.map((pkg) => {
          const unit = quoteProductionCapitalConversion(pkg.id, 1, phasePrice);
          const qty = qtyByPackage[pkg.id] || 0;
          const affordable = unit && unit.costUsd > 0 ? Math.min(maxQty, Math.floor((spendable + 1e-8) / unit.costUsd)) : 0;
          return (
            <div key={pkg.id} className={`rounded-lg border p-3 ${qty > 0 ? 'border-yellow-500 bg-yellow-900/20' : 'border-gray-600 bg-gray-700'}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-semibold text-white">{pkg.name}</div>
                  <p className="text-sm text-gray-300 mt-1">
                    {unit ? `$${unit.costUsd.toFixed(2)} each` : ''} · {pkg.paidShares} paid · Bonus Shares +{formatShares(pkg.investorBonus)} · you receive {formatShares(pkg.paidShares + pkg.investorBonus)} · sponsor +{formatShares(pkg.sponsorBonus)}
                  </p>
                  <p className="text-xs text-blue-200 mt-1">
                    {affordable > 0 ? `Your commission can pay for ${affordable} now.` : 'Your commission cannot pay for one yet. You can still choose it and top up the difference.'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" disabled={disabled || qty <= 0} onClick={() => setQty(pkg.id, qty - 1)} className="h-10 w-10 rounded-lg bg-gray-600 text-white text-xl disabled:opacity-40" aria-label={`Decrease ${pkg.name}`}>−</button>
                  <input
                    type="number"
                    min={0}
                    max={maxQty}
                    value={qty}
                    disabled={disabled}
                    onChange={(e) => setQty(pkg.id, Number(e.target.value))}
                    className="w-16 text-center px-2 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                    aria-label={`${pkg.name} quantity`}
                  />
                  <button type="button" disabled={disabled || qty >= maxQty} onClick={() => setQty(pkg.id, qty + 1)} className="h-10 w-10 rounded-lg bg-gray-600 text-white text-xl disabled:opacity-40" aria-label={`Increase ${pkg.name}`}>+</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-gray-700 rounded-lg p-4 text-sm space-y-1">
        {cart ? cart.lines.map((line) => (
          <div key={line.packageId} className="flex justify-between">
            <span className="text-gray-300">{line.name} × {line.qty}</span>
            <span className="text-white">${line.costUsd.toFixed(2)}</span>
          </div>
        )) : <p className="text-gray-300">Choose a quantity above to build an order.</p>}
        {cart && (
          <>
            <div className="flex justify-between border-t border-gray-600 pt-2"><span className="text-gray-300">Order total</span><span className="text-white">${cart.costUsd.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-gray-300">Paid shares</span><span className="text-yellow-400">{cart.paidShares}</span></div>
            <div className="flex justify-between"><span className="text-gray-300">Bonus Shares</span><span className="text-green-300">+{formatShares(cart.investorBonus)}</span></div>
            <div className="flex justify-between"><span className="text-gray-300">You receive</span><span className="text-yellow-400">{formatShares(cart.investorTotal)}</span></div>
            <div className="flex justify-between"><span className="text-gray-300">Level-1 sponsor</span><span className="text-green-300">+{formatShares(cart.sponsorBonus)}</span></div>
            <div className="flex justify-between"><span className="text-gray-300">{ready ? 'Left in commission' : 'Still needed'}</span><span className={ready ? 'text-blue-300' : 'text-red-300'}>${(ready ? leftover : (shortfall > 0 ? shortfall : Math.max(0, cart.costUsd - spendable))).toFixed(2)}</span></div>
            {pendingTopups > 0 && <p className="text-xs text-blue-200">Pending top-ups of ${pendingTopups.toFixed(2)} are already counted. Convert after an admin approves them.</p>}
          </>
        )}
      </div>

      {cart && cart.ready && onConvert && (
        <button type="button" disabled={disabled} onClick={() => onConvert(cart)} className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-bold py-3 px-4 rounded-lg transition-colors">
          {submitLabel}
        </button>
      )}
      {cart && !cart.ready && shortfall <= 0 && (
        <p className="text-sm text-blue-200">A top-up is already pending for the rest of this order. You can convert after an admin approves it.</p>
      )}
      {cart && shortfall > 0 && onTopUp && (
        <button type="button" disabled={disabled} onClick={() => onTopUp(cart)} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-colors">
          Top up ${shortfall.toFixed(2)} to buy this order
        </button>
      )}
    </div>
  );
};
